import React, { useState, useMemo } from 'react';
import type { GetStaticProps, NextPage } from 'next';
import Head from 'next/head';
import NavbarFooterLayout from '@c/layouts/NavbarFooterLayout';
import { PageHeaderSimple } from '@c/layout/PageHeaderSimple';
import { ControlBar, FilterState } from '@c/insights/ControlBar';
import { DataTableDesktop } from '@c/insights/DataTableDesktop';
import { DataCardsMobile } from '@c/insights/DataCardsMobile';
import { InsightRail } from '@c/insights/InsightRail';
import { CompsModal } from '@c/insights/CompsModal';
import { FinalCTA } from '@c/insights/FinalCTA';
import {
  loadTop50Nov2025,
  getAllBrands,
  getAllCategories,
  type TopSearchItemWithDerived,
  type Condition,
  type Category,
} from '@util/insights/topSearchesNov2025';
import { trackEvent } from '@util/analytics/analytics.service';

interface TopSearchesPageProps {
  items: TopSearchItemWithDerived[];
  brands: string[];
  categories: Category[];
}

const TopSearchesNov2025Page = ({
  items: allItems,
  brands,
  categories,
}: TopSearchesPageProps) => {
  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    selectedCategories: [],
    selectedBrands: [],
    rankScope: 'top50',
    priceRange: [0, 10000],
    sort: 'brand',
  });

  // Modal state
  const [modalItem, setModalItem] = useState<TopSearchItemWithDerived | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Analytics: Track page view on mount
  React.useEffect(() => {
    trackEvent('view_item_list', {
      item_list_name: 'top-searches-nov-2025',
      item_list_id: 'insights-top-50',
    });
  }, []);

  // Filter and sort items
  const filteredItems = useMemo(() => {
    let result = [...allItems];

    // Apply rank scope filter
    const rankLimit =
      filters.rankScope === 'top10' ? 10 : filters.rankScope === 'top25' ? 25 : 50;
    result = result.filter((item) => item.rank <= rankLimit);

    // Apply search filter
    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(
        (item) =>
          item.brand.toLowerCase().includes(searchLower) ||
          item.model.toLowerCase().includes(searchLower)
      );
    }

    // Apply category filter
    if (filters.selectedCategories.length > 0) {
      result = result.filter((item) =>
        filters.selectedCategories.includes(item.category)
      );
    }

    // Apply brand filter
    if (filters.selectedBrands.length > 0) {
      result = result.filter((item) =>
        filters.selectedBrands.includes(item.brand)
      );
    }

    // Apply price range filter
    result = result.filter(
      (item) =>
        item.median_price >= filters.priceRange[0] &&
        item.median_price <= filters.priceRange[1]
    );

    // Apply sorting
    switch (filters.sort) {
      case 'price_desc':
        result.sort((a, b) => b.median_price - a.median_price);
        break;
      case 'brand':
        result.sort((a, b) => a.brand.localeCompare(b.brand));
        break;
      case 'model':
        result.sort((a, b) => a.model.localeCompare(b.model));
        break;
      default:
        break;
    }

    return result;
  }, [allItems, filters]);

  // Handlers
  const handleFilterChange = (newFilters: FilterState) => {
    setFilters(newFilters);
  };

  const handleConditionAdjust = (skuId: string, condition: Condition) => {
    // No-op: condition adjustment handled by component
  };

  const handleCompsClick = (item: TopSearchItemWithDerived) => {
    setModalItem(item);
    setIsModalOpen(true);
  };

  const handleListClick = (
    item: TopSearchItemWithDerived,
    suggestedAsk: number
  ) => {
    // No-op: navigation handled by Link component
  };

  const handleNoteExpand = (skuId: string) => {
    // No-op: note expansion handled by component
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setTimeout(() => setModalItem(null), 300); // Delay clearing to allow close animation
  };

  return (
    <>
      <Head>
        <title>Top 50 Most Searched — November 2025 | GearFocus Seller Insights</title>
        <meta
          name="description"
          content="Proprietary market data for GearFocus sellers. View top searched camera gear with pricing insights and demand indicators."
        />
        <meta name="robots" content="noindex, nofollow" />
        <meta httpEquiv="X-Robots-Tag" content="noindex, nofollow" />
      </Head>

      <div className="min-h-screen bg-gray-50">
        {/* Watermark Overlay */}
        <div className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center opacity-5">
          <div className="text-9xl font-bold text-gray-900 rotate-[-30deg] select-none">
            CONFIDENTIAL
          </div>
        </div>

        {/* Main Content */}
        <div className="relative z-10 max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <PageHeaderSimple
            title="Top 50 Most Searched — November 2025"
            subtitle="Real-time demand data and pricing insights for sellers"
            metaRow={{
              lastUpdated: 'November 25, 2025',
              coverage: 'U.S. & Canada',
              privacy: 'Internal use only',
            }}
            contextNote="This page shows the 50 most-searched camera gear items on GearFocus this month. Use these insights to price competitively and list in-demand products."
          />

          {/* Filters */}
          <ControlBar
            brands={brands}
            categories={categories}
            onFilterChange={handleFilterChange}
          />

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Data Display */}
            <div className="lg:col-span-3">
              <DataTableDesktop
                items={filteredItems}
                onCompsClick={handleCompsClick}
                onListClick={handleListClick}
                onConditionAdjust={handleConditionAdjust}
                onNoteExpand={handleNoteExpand}
              />
              <DataCardsMobile
                items={filteredItems}
                onCompsClick={handleCompsClick}
                onListClick={handleListClick}
                onConditionAdjust={handleConditionAdjust}
                onNoteExpand={handleNoteExpand}
              />
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-4">
                <InsightRail />
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <FinalCTA />
        </div>
      </div>

      {/* Comps Modal */}
      <CompsModal
        item={modalItem}
        isOpen={isModalOpen}
        onClose={handleModalClose}
      />
    </>
  );
};

// Static site generation with 24-hour revalidation
export const getStaticProps: GetStaticProps<TopSearchesPageProps> = async () => {
  const items = await loadTop50Nov2025();
  const brands = getAllBrands(items);
  const categories = getAllCategories();

  return {
    props: {
      items,
      brands,
      categories,
    },
    revalidate: 86400, // 24 hours
  };
};

// Add standard layout with navbar and footer
TopSearchesNov2025Page.getLayout = function getLayout(page: React.ReactElement) {
  return <NavbarFooterLayout>{page}</NavbarFooterLayout>;
};

export default TopSearchesNov2025Page;
